import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, doc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { FaEdit, FaTrash, FaPlus, FaImage } from 'react-icons/fa';
import './Blogs.scss';
import BlogModal from './components/BlogModal';

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const navigate = useNavigate();

  const blogsCollectionRef = collection(db, 'blogs');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const q = query(blogsCollectionRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const blogsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBlogs(blogsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to fetch blogs. Please try again.');
      setLoading(false);
    }
  };

  const handleAddNewBlog = () => {
    setCurrentBlog(null);
    setShowModal(true);
  };

  const handleEditBlog = (blog) => {
    navigate(`/admin/blogs/edit/${blog.id}`);
  };

  const handleDeleteBlog = async (blogId, blogTitle) => {
    const confirmMessage = `Are you sure you want to delete the blog post "${blogTitle}"? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteDoc(doc(db, 'blogs', blogId));
        setBlogs(blogs.filter(blog => blog.id !== blogId));
        
        // Show success message
        setError(null);
        alert(`Blog "${blogTitle}" has been successfully deleted.`);
      } catch (err) {
        console.error('Error deleting blog:', err);
        setError('Failed to delete blog. Please try again.');
      }
    }
  };

  const handleCreateBlog = async (blogData) => {
    try {
      const newBlogRef = await addDoc(blogsCollectionRef, {
        ...blogData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft'
      });
      
      setShowModal(false);
      navigate(`/admin/blogs/edit/${newBlogRef.id}`);
    } catch (err) {
      console.error('Error creating blog:', err);
      setError('Failed to create blog. Please try again.');
    }
  };

  const renderBlogCard = (blog) => {
    return (
      <div className="blog-card" key={blog.id}>
        <div className="blog-card-content">
          <div className="blog-image">
            {blog.featuredImage ? (
              <img src={blog.featuredImage} alt={blog.title} />
            ) : (
              <div className="no-image">
                <FaImage style={{ fontSize: '2rem', opacity: 0.5, marginBottom: '8px' }} />
                <span>No Image</span>
              </div>
            )}
          </div>
          <div className="blog-details">
            <div className="blog-actions">
              <button 
                className="edit-btn" 
                onClick={() => handleEditBlog(blog)}
                aria-label="Edit blog"
              >
                <FaEdit /> Edit
              </button>
              <button 
                className="delete-btn" 
                onClick={() => handleDeleteBlog(blog.id, blog.title)}
                aria-label="Delete blog"
              >
                <FaTrash /> Delete
              </button>
            </div>
            <h3>{blog.title}</h3>
            <div className="blog-meta">
              <span className={`status ${blog.status || 'draft'}`}>
                {blog.status || 'draft'}
              </span>
              <span className="date">
                {blog.createdAt ? new Date(blog.createdAt.seconds * 1000).toLocaleDateString() : 'No date'}
              </span>
            </div>
            {blog.tags && blog.tags.length > 0 && (
              <div className="blog-tags">
                {blog.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="blogs-management-container">
      <div className="page-header">
        <h1>Blogs Management</h1>
        <p>Create, edit, and manage blog posts and articles.</p>
        <button 
          className="add-blog-btn" 
          onClick={handleAddNewBlog}
        >
          <FaPlus /> Add New Blog
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading blogs...</p>
        </div>
      ) : blogs.length === 0 ? (
        <div className="no-blogs">
          <p>No blog posts found. Click "Add New Blog" to create your first post.</p>
        </div>
      ) : (
        <div className="blogs-grid">
          {blogs.map(blog => renderBlogCard(blog))}
        </div>
      )}

      {showModal && (
        <BlogModal 
          onClose={() => setShowModal(false)} 
          onSubmit={handleCreateBlog}
        />
      )}
    </div>
  );
};

export default Blogs; 